import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Wifi, Cpu, Zap, Shield, CheckCircle, Info, ChevronRight, ChevronLeft, Camera, Router, Home, Thermometer } from 'lucide-react';
import { iotScan } from '../../api/client';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const IoTScanWizard = ({ isOpen, onClose, onComplete }) => {
  // Get active workspace from context
  const { activeWorkspace } = useWorkspace();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [protocols, setProtocols] = useState([]);
  const [riskWarning, setRiskWarning] = useState('');
  const [formData, setFormData] = useState({
    name: '', // Optional scan name
    target: '',
    workspace_id: '', // Will be auto-set from context
    protocols: ['mqtt', 'http', 'telnet'],
    scan_type: 'discovery',
    accept_risk: false
  });

  // Auto-set workspace_id from active workspace
  useEffect(() => {
    if (activeWorkspace?.id && !formData.workspace_id) {
      setFormData(prev => ({ ...prev, workspace_id: activeWorkspace.id }));
    }
  }, [activeWorkspace?.id]);

  useEffect(() => {
    if (isOpen) {
      fetchProtocols();
      fetchRiskWarning();
    }
  }, [isOpen]);

  const fetchProtocols = async () => {
    try {
      const data = await iotScan.getProtocols();
      setProtocols(data);
    } catch (error) {
      console.error('Failed to fetch IoT protocols:', error);
      // Fallback protocols
      setProtocols([
        { name: 'mqtt', display_name: 'MQTT', ports: [1883, 8883], risk: 'high', description: 'Message Queue - IoT messaging protocol', implemented: true },
        { name: 'coap', display_name: 'CoAP', ports: [5683, 5684], risk: 'medium', description: 'Constrained Application Protocol', implemented: true },
        { name: 'upnp', display_name: 'UPnP/SSDP', ports: [1900, 5000], risk: 'high', description: 'Device discovery and control', implemented: true },
        { name: 'rtsp', display_name: 'RTSP', ports: [554, 8554], risk: 'high', description: 'Streaming Protocol - IP cameras', implemented: true },
        { name: 'telnet', display_name: 'Telnet', ports: [23], risk: 'critical', description: 'Legacy unencrypted protocol', implemented: true },
        { name: 'http', display_name: 'HTTP/HTTPS', ports: [80, 443, 8080], risk: 'medium', description: 'Web interfaces for IoT devices', implemented: true },
        { name: 'modbus', display_name: 'Modbus TCP', ports: [502], risk: 'high', description: 'Industrial IoT protocol', implemented: true },
        { name: 'ble', display_name: 'BLE', ports: [], risk: 'medium', description: 'Bluetooth Low Energy - Requires adapter', implemented: true },
      ]);
    }
  };

  const fetchRiskWarning = async () => {
    try {
      const data = await iotScan.getRiskWarning();
      setRiskWarning(data.warning);
    } catch (error) {
      setRiskWarning(`AVERTISSEMENT IoT SCAN

IoT scans can affect sensitive equipment:
- Surveillance cameras
- Home automation systems
- Capteurs industriels
- Routeurs et gateways

Risques potentiels:
- Interruption de services critiques
- Device disconnections
- Security alerts triggered

Recommandations:
- Obtain written authorization
- Commencez par un scan en mode discovery
- Avoid aggressive scans on production systems
- Have a rollback plan ready`);
    }
  };

  const toggleProtocol = (protocolName) => {
    setFormData(prev => ({
      ...prev,
      protocols: prev.protocols.includes(protocolName)
        ? prev.protocols.filter(p => p !== protocolName)
        : [...prev.protocols, protocolName]
    }));
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'critical': return 'text-purple-400 border-purple-500 bg-purple-500/10';
      case 'high': return 'text-red-400 border-red-500 bg-red-500/10';
      case 'medium': return 'text-orange-400 border-orange-500 bg-orange-500/10';
      case 'low': return 'text-yellow-400 border-yellow-500 bg-yellow-500/10';
      default: return 'text-gray-400 border-gray-500 bg-gray-500/10';
    }
  };

  const getProtocolIcon = (protocol) => {
    switch (protocol) {
      case 'rtsp': return Camera;
      case 'upnp': return Router;
      case 'mqtt':
      case 'coap': return Wifi;
      case 'http': return Home;
      case 'ble': return Thermometer;
      default: return Cpu;
    }
  };

  const handleSubmit = async () => {
    if (!formData.accept_risk) {
      alert('Vous devez accepter les risques pour continuer');
      return;
    }

    setLoading(true);
    try {
      const result = await iotScan.create(formData);
      onComplete(result);
      onClose();
    } catch (error) {
      console.error('Failed to create IoT scan:', error);
      // Check for insufficient credits error (402)
      if (error.response?.status === 402) {
        const detail = error.response?.data?.detail;
        if (detail?.error === 'insufficient_credits') {
          alert(`Insufficient credits to launch this scan.\n\nCredits required: ${detail.message || 'N/A'}\n\nPlease purchase credits or upgrade your plan.`);
        } else {
          alert('Insufficient credits. Please purchase credits to continue.');
        }
      } else {
        alert('Error creating IoT scan: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-brand-black-light border border-cyan-500/50 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-900/50 to-blue-900/50 border-b border-cyan-500/30 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-cyan-500/20 rounded-xl flex items-center justify-center border border-cyan-500/50">
                <Wifi className="w-8 h-8 text-cyan-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  IoT Scan
                  <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded border border-cyan-500/50">
                    SMART DEVICES
                  </span>
                </h2>
                <p className="text-cyan-300/80 text-sm mt-1">Scan connected IoT equipment</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-6">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  step >= s ? 'bg-cyan-500 text-white' : 'bg-gray-700 text-gray-400'
                }`}>
                  {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-cyan-500' : 'bg-gray-700'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-250px)]">
          {/* Step 1: Risk Warning */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-10 h-10 text-yellow-400 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-yellow-400 mb-4">AVERTISSEMENT IoT</h3>
                    <pre className="text-yellow-200/80 whitespace-pre-wrap text-sm font-sans leading-relaxed">
                      {riskWarning}
                    </pre>
                  </div>
                </div>
              </div>

              <div className="bg-cyan-900/20 border border-cyan-500/30 rounded-xl p-4">
                <h4 className="text-cyan-400 font-semibold mb-3">Detectable equipment types</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { icon: Camera, label: 'Cameras IP' },
                    { icon: Router, label: 'Routeurs/Gateways' },
                    { icon: Home, label: 'Domotique' },
                    { icon: Thermometer, label: 'Capteurs' },
                  ].map(({ icon: Icon, label }) => (
                    <div key={label} className="flex items-center gap-2 text-gray-300 text-sm">
                      <Icon className="w-4 h-4 text-cyan-400" />
                      {label}
                    </div>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-3 p-4 bg-brand-black rounded-lg border border-gray-700 cursor-pointer hover:border-cyan-500/50 transition-colors">
                <input
                  type="checkbox"
                  checked={formData.accept_risk}
                  onChange={(e) => setFormData(prev => ({ ...prev, accept_risk: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-600 text-cyan-500 focus:ring-cyan-500"
                />
                <span className="text-white">
                  I have read and accept the risks associated with the IoT scan.
                  I have legal authorization to scan these devices.
                </span>
              </label>
            </div>
          )}

          {/* Step 2: Target & Protocols */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Scan Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nom du scan (optionnel)
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Audit IoT Bureau, Test Smart Devices..."
                  className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Give a custom name to easily identify this scan
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Cible(s) - IP, CIDR ou plage
                </label>
                <input
                  type="text"
                  value={formData.target}
                  onChange={(e) => setFormData(prev => ({ ...prev, target: e.target.value }))}
                  placeholder="192.168.1.0/24 ou 192.168.1.1-50"
                  className="w-full px-4 py-3 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Formats supportes: IP unique, CIDR (10.0.0.0/24), plage (10.0.0.1-100), liste (IP1,IP2,IP3)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Protocoles IoT a scanner
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {protocols.map((protocol) => {
                    const Icon = getProtocolIcon(protocol.name);
                    return (
                      <button
                        key={protocol.name}
                        onClick={() => protocol.implemented && toggleProtocol(protocol.name)}
                        disabled={!protocol.implemented}
                        className={`p-4 rounded-lg border text-left transition-all ${
                          !protocol.implemented
                            ? 'opacity-50 cursor-not-allowed border-gray-700 bg-gray-800/50'
                            : formData.protocols.includes(protocol.name)
                              ? 'border-cyan-500 bg-cyan-500/20'
                              : 'border-gray-700 hover:border-cyan-500/50 bg-brand-black'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${formData.protocols.includes(protocol.name) ? 'text-cyan-400' : 'text-gray-400'}`} />
                            <span className="font-bold text-white uppercase">{protocol.name}</span>
                          </div>
                          <span className={`px-2 py-0.5 text-xs rounded border ${getRiskColor(protocol.risk)}`}>
                            {protocol.risk}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400">{protocol.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {protocol.ports?.length > 0 ? `Ports: ${protocol.ports.join(', ')}` : 'Pas de port TCP/UDP'}
                          {!protocol.implemented && ' - Not implemented'}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Type de scan
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'discovery', label: 'Discovery', desc: 'IoT equipment detection', icon: Wifi },
                    { value: 'enumeration', label: 'Enumeration', desc: 'Details and services', icon: Info },
                    { value: 'vulnerability', label: 'Vulnerabilities', desc: 'Full security analysis', icon: Zap },
                  ].map(({ value, label, desc, icon: Icon }) => (
                    <button
                      key={value}
                      onClick={() => setFormData(prev => ({ ...prev, scan_type: value }))}
                      className={`p-4 rounded-lg border text-left transition-all ${
                        formData.scan_type === value
                          ? 'border-cyan-500 bg-cyan-500/20'
                          : 'border-gray-700 hover:border-cyan-500/50 bg-brand-black'
                      }`}
                    >
                      <Icon className={`w-6 h-6 mb-2 ${formData.scan_type === value ? 'text-cyan-400' : 'text-gray-400'}`} />
                      <div className="font-semibold text-white">{label}</div>
                      <p className="text-xs text-gray-400 mt-1">{desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-brand-black rounded-xl border border-gray-700 overflow-hidden">
                <div className="bg-gray-800/50 px-4 py-3 border-b border-gray-700">
                  <h3 className="font-semibold text-white">IoT Scan Summary</h3>
                </div>
                <div className="p-4 space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Cible</span>
                    <span className="text-white font-mono">{formData.target || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Protocoles</span>
                    <div className="flex flex-wrap gap-2 justify-end">
                      {formData.protocols.map(p => (
                        <span key={p} className="px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded border border-cyan-500/50 uppercase">
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Type de scan</span>
                    <span className="text-white capitalize">{formData.scan_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Risks accepted</span>
                    <span className={formData.accept_risk ? 'text-green-400' : 'text-red-400'}>
                      {formData.accept_risk ? 'Oui' : 'Non'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-cyan-900/20 border border-cyan-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-cyan-200/80">
                    <p className="font-semibold mb-1">Security tip</p>
                    <p>IoT devices often have default credentials. The scan will detect exposed services and known vulnerabilities.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-700 p-4 bg-gray-900/50 flex justify-between">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="px-6 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            {step === 1 ? 'Annuler' : 'Retour'}
          </button>

          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && !formData.accept_risk}
              className="px-6 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              Suivant
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.target || formData.protocols.length === 0}
              className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Lancement...
                </>
              ) : (
                <>
                  <Wifi className="w-4 h-4" />
                  Lancer le Scan IoT
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default IoTScanWizard;
